import { captureException } from '@sentry/browser'
import { parseWebPushConfig } from './web_push/config'
import {
  type PushRegistration,
  setupPushPrompt,
  setupPushRegistration
} from './web_push/controller'

// Private types ---------------------------------------------------------------

type PushHost = {
  readonly el: HTMLElement
  readonly pushEvent: (
    name: string,
    payload: { readonly token: string }
  ) => unknown
}

type PromptHost = { readonly el: HTMLElement }

// Constants -------------------------------------------------------------------

const TOKEN_COOKIE = 'web_push_token'
const config = parseWebPushConfig(
  process.env.FIREBASE_WEB_CONFIG,
  process.env.FIREBASE_VAPID_KEY
)
const registrations = new WeakMap<PushHost, PushRegistration>()
const prompts = new WeakMap<PromptHost, () => void>()

// Private functions -----------------------------------------------------------

// Checks browser capabilities without downloading or initializing Firebase.
const supported = (): boolean =>
  config !== null && 'Notification' in window && 'serviceWorker' in navigator

// Imports the SDK only after notification permission has been granted.
const fetchToken = async (signal: AbortSignal): Promise<string | null> => {
  if (config === null || signal.aborted) {
    return null
  }

  const client = await import('./web_push/client')

  return signal.aborted ? null : client.fetchToken(config, signal)
}

// Retains the browser token so the existing logout request can unbind it.
const rememberToken = (token: string): void => {
  // biome-ignore lint/suspicious/noDocumentCookie: cookieStore is missing in Safari before 18.4
  document.cookie = `${TOKEN_COOKIE}=${token}; path=/; SameSite=Lax`
}

// Reports optional registration failures without including token or configuration data.
const reportError = (error: unknown): void => {
  captureException(error, { tags: { component: 'WebPush' } })
}

// Public hooks ----------------------------------------------------------------

export const WebPush = {
  // Refreshes granted permission, or waits for an explicit Enable click.
  mounted(this: PushHost): void {
    if (!supported()) {
      return
    }

    registrations.set(
      this,
      setupPushRegistration(this.el, {
        permission: () => Notification.permission,
        requestPermission: () => Notification.requestPermission(),
        fetchToken,
        register: (token) => {
          rememberToken(token)
          this.pushEvent('web_push:register', { token })
        },
        reportError
      })
    )
  },

  // Prevents late registration from writing to a disconnected LiveView.
  disconnected(this: PushHost): void {
    registrations.get(this)?.disconnect()
  },

  // Retries granted permission using the current connection.
  reconnected(this: PushHost): void {
    registrations.get(this)?.reconnect()
  },

  // Removes the enable listener and invalidates pending registration.
  destroyed(this: PushHost): void {
    registrations.get(this)?.destroy()
    registrations.delete(this)
  }
}

export const WebPushPrompt = {
  // Reveals the prompt using browser permission state alone.
  mounted(this: PromptHost): void {
    if (!(supported() && 'permissions' in navigator)) {
      return
    }

    prompts.set(
      this,
      setupPushPrompt(this.el, () =>
        navigator.permissions.query({ name: 'notifications' })
      )
    )
  },

  // Prevents a late permission reply from reviving a removed prompt.
  destroyed(this: PromptHost): void {
    prompts.get(this)?.()
    prompts.delete(this)
  }
}
